// Xerath Arcane Barrage VFX — League Q feel:
// 1. Blue ground indicator (rectangle + target circles) during windup
// 2. Jagged lightning fires through all targets in a line

import type { Animation } from "../../engine/animation/AnimationQueue";
import type { Point } from "../../engine/hex/HexLayout";

const WINDUP_MS = 500;
const BOLT_MS   = 110;
const FLASH_MS  = 200;
const TOTAL     = WINDUP_MS + BOLT_MS + FLASH_MS;

type Segment = { x1: number; y1: number; x2: number; y2: number };

function buildBolt(dist: number, jags: number, seed: number): Segment[] {
  const pts: { x: number; y: number }[] = [{ x: 0, y: 0 }];
  const seeded = (n: number) => Math.sin(seed + n * 7.3) * 0.5 + 0.5;
  for (let i = 1; i < jags; i++) {
    const t = i / jags;
    pts.push({ x: dist * t, y: (seeded(i) - 0.5) * dist * 0.28 });
  }
  pts.push({ x: dist, y: 0 });
  return pts.slice(0, -1).map((p, i) => ({
    x1: p.x, y1: p.y, x2: pts[i + 1].x, y2: pts[i + 1].y,
  }));
}

export class XerathBoltVFX implements Animation {
  private elapsed = 0;
  private primaryAngle: number;
  private totalDist: number;
  private boltSegs: Segment[];
  // each target's projected distance along the beam axis
  private projectedDists: number[];

  constructor(
    private from: Point,
    private targets: Point[],
  ) {
    const bx = (t: Point) => t.x - from.x;
    const by = (t: Point) => t.y - from.y;

    // Sort targets by distance from caster
    const sorted = [...targets].sort((a, b) => {
      const da = Math.sqrt(bx(a) ** 2 + by(a) ** 2);
      const db = Math.sqrt(bx(b) ** 2 + by(b) ** 2);
      return da - db;
    });
    this.targets = sorted;

    const far = sorted[sorted.length - 1];
    const dx = bx(far), dy = by(far);
    this.primaryAngle = Math.atan2(dy, dx);
    this.totalDist    = Math.sqrt(dx * dx + dy * dy);

    // Project each target onto the beam axis for staggered impacts
    const ax = Math.cos(this.primaryAngle), ay = Math.sin(this.primaryAngle);
    this.projectedDists = sorted.map(t => bx(t) * ax + by(t) * ay);

    this.boltSegs = buildBolt(this.totalDist, 9, 2.4);
  }

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, TOTAL);
  }

  draw(ctx: CanvasRenderingContext2D): void {
    // Ground indicator — shown during windup, fades as bolt fires
    if (this.elapsed < WINDUP_MS + BOLT_MS * 0.4) {
      const inT  = Math.min(this.elapsed / 120, 1);           // fade-in over 120ms
      const outT = Math.max(0, (this.elapsed - WINDUP_MS) / (BOLT_MS * 0.4));
      const alpha = inT * (1 - outT);
      this.drawGroundIndicator(ctx, alpha);
    }

    // Bolt + impacts
    if (this.elapsed >= WINDUP_MS) {
      const beamElapsed = this.elapsed - WINDUP_MS;
      ctx.save();
      ctx.translate(this.from.x, this.from.y);
      ctx.rotate(this.primaryAngle);

      if (beamElapsed < BOLT_MS) {
        this.drawBolt(ctx, beamElapsed / BOLT_MS, 1);
      } else {
        const fadeT = (beamElapsed - BOLT_MS) / FLASH_MS;
        // bolt lingers briefly
        if (fadeT < 0.25) this.drawBolt(ctx, 1, 1 - fadeT * 4);
        this.drawImpactFlashes(ctx, 1 - fadeT);
      }

      ctx.restore();
    }
  }

  // Ground indicator drawn in world space (indicator rect in rotated caster space)
  private drawGroundIndicator(ctx: CanvasRenderingContext2D, alpha: number): void {
    const pulse = 0.72 + 0.28 * Math.sin(this.elapsed / 65);
    const a = alpha * pulse;

    // Beam-path rectangle in rotated caster space
    ctx.save();
    ctx.translate(this.from.x, this.from.y);
    ctx.rotate(this.primaryAngle);

    const h = 26;
    ctx.globalAlpha = a * 0.18;
    ctx.fillStyle   = "#55ccff";
    ctx.shadowColor = "#2288ff";
    ctx.shadowBlur  = 20;
    ctx.fillRect(0, -h / 2, this.totalDist, h);

    ctx.globalAlpha = a * 0.8;
    ctx.strokeStyle = "#33aaff";
    ctx.lineWidth   = 1.5;
    ctx.shadowBlur  = 14;
    ctx.strokeRect(0, -h / 2, this.totalDist, h);

    ctx.restore();

    // Pulsing circles at each target's world position
    for (const t of this.targets) {
      ctx.save();
      ctx.globalAlpha = a * 0.9;
      ctx.strokeStyle = "#55ddff";
      ctx.lineWidth   = 2;
      ctx.shadowColor = "#1177ff";
      ctx.shadowBlur  = 22;
      ctx.beginPath();
      ctx.arc(t.x, t.y, 18 + pulse * 4, 0, Math.PI * 2);
      ctx.stroke();

      ctx.globalAlpha = a * 0.12;
      ctx.fillStyle   = "#88eeff";
      ctx.fill();
      ctx.restore();
    }
  }

  private drawBolt(ctx: CanvasRenderingContext2D, t: number, alpha: number): void {
    const drawEnd = this.totalDist * t;

    ctx.save();

    // Outer glow
    ctx.globalAlpha = alpha * 0.4;
    ctx.strokeStyle = "#cc88ff";
    ctx.lineWidth   = 7;
    ctx.lineCap     = "round";
    ctx.shadowColor = "#9933ff";
    ctx.shadowBlur  = 26;
    this.strokeSegs(ctx, drawEnd);

    // Core bolt
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = "#ddeeff";
    ctx.lineWidth   = 2.5;
    ctx.shadowColor = "#aa66ff";
    ctx.shadowBlur  = 14;
    this.strokeSegs(ctx, drawEnd);

    // Bright white center
    ctx.globalAlpha = alpha * 0.9;
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth   = 0.9;
    ctx.shadowBlur  = 6;
    this.strokeSegs(ctx, drawEnd);

    ctx.restore();
  }

  private strokeSegs(ctx: CanvasRenderingContext2D, maxDist: number): void {
    let drawn = 0;
    for (const seg of this.boltSegs) {
      if (drawn >= maxDist) break;
      const segLen = Math.sqrt((seg.x2 - seg.x1) ** 2 + (seg.y2 - seg.y1) ** 2);
      const avail  = maxDist - drawn;
      ctx.beginPath();
      ctx.moveTo(seg.x1, seg.y1);
      if (avail < segLen) {
        const ft = avail / segLen;
        ctx.lineTo(seg.x1 + (seg.x2 - seg.x1) * ft, seg.y1 + (seg.y2 - seg.y1) * ft);
        ctx.stroke();
        break;
      }
      ctx.lineTo(seg.x2, seg.y2);
      ctx.stroke();
      drawn += segLen;
    }
  }

  private drawImpactFlashes(ctx: CanvasRenderingContext2D, fade: number): void {
    for (const d of this.projectedDists) {
      ctx.save();
      ctx.globalAlpha = fade * 0.8;
      ctx.fillStyle   = "#eeddff";
      ctx.shadowColor = "#aa44ff";
      ctx.shadowBlur  = 30;
      ctx.beginPath();
      ctx.arc(d, 0, 5 * fade + 2, 0, Math.PI * 2);
      ctx.fill();

      ctx.globalAlpha = fade * 0.45;
      ctx.strokeStyle = "#88bbff";
      ctx.lineWidth   = 2;
      ctx.beginPath();
      ctx.arc(d, 0, 5 + (1 - fade) * 18, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL;
  }
}
